import { select, takeEvery, put } from 'redux-saga/effects';

import { List as IList } from 'immutable';
import { getCapiSessionID } from 'capi/redux/immutable';
import { STEP_CHANGE } from '../routing/routingModule';
import { getAnalyticsPathName, getBaseEventProperties } from './segmentSelectors';
import { trackPage, trackEvent, trackIdentify } from './segment';
import { SEGMENT_IP, AUTH_REASON_CODE } from '../constants/constants';
import { ORDER_CANCELLATION, CONFIRMATION_STEP } from '../routing/flowConstants';
import { getIntlCurrencyCode, getIntlLocale } from '../intl/intlSelectors';
import {
    getAllActiveCards,
    getPaymentEGiftCards,
    getPaymentPhysicalGiftCards
} from '../productCartHandler/productCartSelectors';
import { setLastPurchaseType, setLastCardType } from './segmentModule';
import { buildHotjarTraits } from './hotjarAttributes';

let pagePreviousUrl = document.referrer || '';
let referrer;

/**
 * Send a segment 'page' event triggered by a router action.
 * Make sure we don't record the user's IP. The user's IP is PII
 * with regards to GDPR and we don't want to record it.
 *
 * This function expects to receive a STEP_CHANGE action from our flow module.
 * @param {string} path - the path of the new step ("page")
 * @param {Object} location - the location object containing additional information
 */
export function* sendRoutingEvent({ path, location }) {
    let name = getAnalyticsPathName(path);
    const { state = {} } = location;

    if (name) {
        let allItems = IList();
        if (name === 'Cart Page') {
            allItems = yield select(getAllActiveCards);
        } else if (name === 'Payment') {
            const eGiftCards = yield select(getPaymentEGiftCards);
            const physicalCards = yield select(getPaymentPhysicalGiftCards);
            allItems = eGiftCards.concat(physicalCards);
        }

        
        if (name === 'Cart Page' || name === 'Payment') {
            if (allItems && allItems.size > 0) {
                const sortedItems = allItems.sortBy(item => new Date(item.get('deliveryDate')).getTime());
                const latestItem = sortedItems.last();
                if (latestItem) {
                    const purchaseType = latestItem.get('selfBuy') ? 'SELF' : 'GIFT';
                    yield put(setLastPurchaseType(purchaseType));
                    const cardType = latestItem.get('itemCardType') === 'Physical' ? 'Plastic' : 'Digital';
                    yield put(setLastCardType(cardType));
                }
            }
        }

        
        
        
        
        if (path === ORDER_CANCELLATION && state && state.lastAuthReasonCode) {
            const { lastAuthReasonCode } = state;
            if (lastAuthReasonCode === AUTH_REASON_CODE.PREAUTH_REJECTED) {
                name = `${name} | Pre Auth Reject`;
            } else if (lastAuthReasonCode === AUTH_REASON_CODE.RISK_REJECTED) {
                name = `${name} | Post Auth Reject`;
            }
        }

        
        
        if (path === CONFIRMATION_STEP && state && state.lastAuthReasonCode === AUTH_REASON_CODE.RISK_REVIEW) {
            name = `${name} | Pending Review`;
        }

        const baseProperties = yield select(getBaseEventProperties);
        const currency = yield select(getIntlCurrencyCode);
        const language = yield select(getIntlLocale);
        const properties = {
            ...baseProperties,
            
            
            
            cashStarSessionId: (state && state.sessionKey) || baseProperties.cashStarSessionId
        };

        const segmentProperties = {
            name,
            properties
        };
        
        referrer = pagePreviousUrl;
        trackPage(name, segmentProperties, {
            context: {
                ip: SEGMENT_IP,
                page: {
                    referrer,
                    currency,
                    language
                }
            }
        });
        pagePreviousUrl = window.location.href;
    }
}

export function* sendTrackingEvent(action, data) {
    const baseProperties = yield select(getBaseEventProperties);
    const preexistingDataProperties = data.properties || {};
    data.properties = { ...baseProperties, ...preexistingDataProperties };
    trackEvent(action, data, { context: { ip: SEGMENT_IP, page: { referrer } } });

    const reduxState = yield select();
    const capiSessionId = getCapiSessionID(reduxState);
    if (capiSessionId) {
        const traits = buildHotjarTraits(data, reduxState, action);
        if (Object.keys(traits).length > 0) {
            trackIdentify(capiSessionId, traits);
        }
    }
}

export function* sendSegmentEvents(action) {
    const { eventAction, eventData } = action.meta.segment;
    yield sendTrackingEvent(eventAction, eventData);
}

export const takeGeneralPattern = action => ((action.meta || {}).segment !== undefined);

export function* segmentWatcher() {
    yield takeEvery(takeGeneralPattern, sendSegmentEvents);
    yield takeEvery(STEP_CHANGE, sendRoutingEvent);
}
